import styles from './ChartManager.module.scss';
import { PropsWithChildren, ReactElement } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { NoResults } from '@dmc/ui';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import 'chartjs-adapter-luxon';
import { ProgressIndicatorCircular } from '@dmc/ui';

// Register all Chart.js classes used throughout the app.
ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface ChartManagerProps {
  isLoading: boolean;
  isFetching: boolean;
  hasData: boolean;
  height: number;
  fallbackHeight?: number;
  trimChartHeight?: boolean;
}

export const ChartManager = ({
  isLoading,
  isFetching,
  hasData,
  height,
  fallbackHeight,
  trimChartHeight = false,
  children,
}: PropsWithChildren<ChartManagerProps>): ReactElement => {
  const { t } = useTranslation();

  if (!isLoading) {
    if (hasData) {
      return (
        <div className={`${styles.container} ${trimChartHeight && styles['container--trim']} `}>
          <div
            className={`${styles.chart} ${isFetching && styles['chart--fetching']}`}
            style={{ height }}
          >
            {children}
          </div>
          {isFetching && (
            <div className={styles.loadingOverlay}>
              <ProgressIndicatorCircular small />
            </div>
          )}
        </div>
      );
    } else if (!isFetching) {
      return (
        <div className={styles.noResults}>
          <NoResults description={t('NoResults')} height={fallbackHeight} />
        </div>
      );
    }
  }

  return <Skeleton containerClassName={styles.wrapperFix} height={fallbackHeight ?? height} />;
};

export default ChartManager;
